// client/src/services/lexistarTrips.js
//
// LexiStar trips / runs / deviations / status board / events API client (ST-00188, M2-A2;
// contracts docs/lexistar/contracts/m2-contracts.md §4.2, §2.4). Server:
// server/modules/lexistar/trips and server/modules/lexistar/events, mounted at
// /api/lexistar.
//
// Thin wrapper over the shared axios instance in `services/api` (cookie-session auth,
// CSRF header, single-flight 401 refresh; `api` already carries `/api`). Every helper
// resolves to the response BODY. Payloads use the DB column names (snake_case). Action
// routes use the contract's `:verb` suffix literally (POST /trips/:id:dispatch).
//
// Response shapes (server side of what client/src/services/lexistarStatusBoard.js reads):
//   listTrips          { trips: [trip row + access_type, source/destination names, legs[]], pagination }
//   getTrip            { trip (with legs), legs, track (GeoJSON LineString | null), timeline,
//                        deviations, ticket_matches, fuel { budget_original_l, budget_adjusted_l,
//                        consumed_l, consumed_provenance, pinned, legs, refuels }, run, can_change }
//   createTrip         { trip (PLANNED, or DISPATCHED with dispatch:true), legs, flags, budget,
//                        command|null, warnings[] }  — the server computes the legs and
//                        budget_original_l (LS-04 §9) and pins norm / price / rate-card ids
//   createTripsBatch   { created, trips[] }  — all or nothing (4xx names the failing index)
//   dispatch/cancel/abort/overrideStage   { trip, legs, command, outbox, warnings }
//   createRun          { run, trips[] }
//   appendTripToRun    { trip, legs, run, command, warnings }  ("Next load from here")
//   getRunSummary      { run, trips[], turnarounds[], totals, computed, next_source, active_trip_id }
//   listDeviations     { deviations: [deviation + trip{…} + position + track + sla_due_at +
//                        elapsed_s + can_decide], pagination }
//   approve/reject/escalate   { deviation, command, outbox }
//   getStatusBoard     { view:'asset', rows[], summary, generated_at } |
//                      { view:'run', runs[], groups[], summary, generated_at }
//                      rows carry status.override (FR-STAT-004), dwell_breach + dwell[]
//                      (FR-STAT-007), speed_limit_kmh (null until a limit source exists)
//   setAssetOverride   { override|null, command, outbox }
//   listEvents         { events: EventV1[], pagination, window }
//   acknowledgeEvent   { event, acknowledged_by }
//
// Realtime: joinLexiStarRooms() uses the /tracking handlers that scope rooms by the
// socket's own session (the server ignores a tenant it may not see).

import api from './api';

const LX = '/lexistar';

/** Drop undefined / null / '' so the API never sees `stage=`. */
const clean = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );
const body = (res) => res.data;
/** Arrays become comma lists (stage, state, type, … accept either). */
const query = (params = {}) =>
  clean(
    Object.fromEntries(
      Object.entries(params).map(([k, v]) => [k, Array.isArray(v) ? v.join(',') : v])
    )
  );

// ── Trips ────────────────────────────────────────────────────────────────────

/** Filters: stage (list), asset (id | asset key | registration), run_id, date (YYYY-MM-DD), from, to, q, page, limit. */
export const listTrips = (params) => api.get(`${LX}/trips`, { params: query(params) }).then(body);

export const getTrip = (id) => api.get(`${LX}/trips/${id}`).then(body);

/**
 * Create a trip. Asset: asset_id | asset_key | registration. Loaded leg: route_id
 * [+ route_version] | lane_id | from_location_id + to_location_id (planned_km when there is
 * no route and no coordinates). Optional positioning { from_location_id | from_last_known |
 * lat, lon } and closing { to_location_id } legs; load { material, order_ref, consignor,
 * consignee, planned_tonnes }; planned_start; contingency_pct; idle_allowance_h (default
 * 0.75 h per site visit); budget_override { litres, reason } (FR-TRIP-002: the computed
 * value stays in pinned.budget_computed_l); channel (FR-TRIP-005, e.g. 'whatsapp');
 * dispatch: true to plan and dispatch at once (needs lexistar.trips.dispatch).
 */
export const createTrip = (data) => api.post(`${LX}/trips`, data).then(body);

export const createTripsBatch = (trips, { dispatch } = {}) =>
  api.post(`${LX}/trips:batch`, clean({ trips, dispatch })).then(body);

export const dispatchTrip = (id, { version } = {}) =>
  api.post(`${LX}/trips/${id}:dispatch`, clean({ version })).then(body);

export const cancelTrip = (id, reason, { version } = {}) =>
  api.post(`${LX}/trips/${id}:cancel`, clean({ reason, version })).then(body);

export const abortTrip = (id, reason, { version } = {}) =>
  api.post(`${LX}/trips/${id}:abort`, clean({ reason, version })).then(body);

/**
 * Manual stage change (lexistar.trips.manage): { stage, reason, reason_code?, until?, version? };
 * `until` (ISO, future) is when the override lapses (stored as trip.pinned.override).
 */
export const overrideStage = (id, data) =>
  api.post(`${LX}/trips/${id}:overrideStage`, clean(data)).then(body);

/**
 * Retrospective trip from a detected load (FR-TRIP-023, origin 'prompted'):
 * { asset…, from_location_id, detected_at, to_location_id | route_id | lane_id, load?,
 *   event_id?, ticket_snapshot_id?, run_id? }.
 */
export const createTripFromDetectedLoad = (data) =>
  api.post(`${LX}/trips:fromDetectedLoad`, clean(data)).then(body);

// ── Runs ─────────────────────────────────────────────────────────────────────

/** { asset…, driver_id?, driver_tag?, shift_date?, template_ref?, trips?: [trip plans] } */
export const createRun = (data) => api.post(`${LX}/runs`, clean(data)).then(body);

/**
 * "Next load from here" (FR-TRIP-021): the source defaults to the run's last offloading
 * site; dispatch defaults to true.
 */
export const appendTripToRun = (runId, data) =>
  api.post(`${LX}/runs/${runId}:appendTrip`, clean(data)).then(body);

export const getRunSummary = (runId) => api.get(`${LX}/runs/${runId}/summary`).then(body);

// ── Deviations (approvals queue) ─────────────────────────────────────────────

/** state: list (default the undecided ones), trip_id, page, limit. */
export const listDeviations = (params) =>
  api.get(`${LX}/deviations`, { params: query(params) }).then(body);

/** { reason_code, note?, create_corridor?: { scope: 'trip'|'route', expiry }, extra_km?, version? } */
export const approveDeviation = (id, data) =>
  api.post(`${LX}/deviations/${id}:approve`, clean(data)).then(body);

/** { reason_code, note?, version? } */
export const rejectDeviation = (id, data) =>
  api.post(`${LX}/deviations/${id}:reject`, clean(data)).then(body);

/** { reason_code?, note?, version? } */
export const escalateDeviation = (id, data = {}) =>
  api.post(`${LX}/deviations/${id}:escalate`, clean(data)).then(body);

// ── Status board ─────────────────────────────────────────────────────────────

/** view: 'asset' (default) | 'run'. */
export const getStatusBoard = (params) =>
  api.get(`${LX}/status-board`, { params: query(params) }).then(body);

/**
 * FR-STAT-004 truck override: mode 'UNDER_MAINTENANCE' | 'OUT_OF_SERVICE' | 'STANDBY', or
 * null to clear; reason required with a mode; until optional (ISO, future).
 * @param {string} assetKey e.g. 'reg:JX12KLGP'
 */
export const setAssetOverride = (assetKey, data) =>
  api.post(`${LX}/status-board/${assetKey}:override`, data).then(body);

// ── Events ───────────────────────────────────────────────────────────────────

/**
 * FR-EVT-002 filters: from, to, category, type, severity, status, source (lists), asset,
 * driver, trip_id, route_id, geofence_id, q, page, limit (default window: last 7 days).
 */
export const listEvents = (params) => api.get(`${LX}/events`, { params: query(params) }).then(body);

export const acknowledgeEvent = (id, data = {}) =>
  api.post(`${LX}/events/${id}:acknowledge`, clean(data)).then(body);

// ── Realtime rooms (§2.4) ────────────────────────────────────────────────────

export const LEXISTAR_EVENT = 'lexistar:event';

/**
 * Join the LexiStar rooms on an already-connected /tracking socket. The server scopes
 * every room by the socket's own session: `tenant:{own}`, `tenant:{own}:approvals`
 * (ROUTE.DEVIATION_*), `trip:{id}` (only when the trip is visible to the caller).
 * Resolves to the server's acks.
 * @param {import('socket.io-client').Socket} socket
 * @param {{approvals?: boolean, tripId?: string|null}} opts
 */
export function joinLexiStarRooms(socket, { approvals = false, tripId = null } = {}) {
  const emit = (event, arg) =>
    new Promise((resolve) => {
      socket.emit(event, arg, (ack) => resolve({ event, ...(ack || {}) }));
    });
  const joins = [emit('lexistar:join', {})];
  if (approvals) joins.push(emit('join:approvals', {}));
  if (tripId) joins.push(emit('join:trip', { tripId }));
  return Promise.all(joins);
}

/** Leave the approvals and/or trip rooms joined above. */
export function leaveLexiStarRooms(socket, { approvals = false, tripId = null } = {}) {
  if (approvals) socket.emit('leave:approvals', {});
  if (tripId) socket.emit('leave:trip', { tripId });
}
